import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';
import Comments from '../components/ui/Comments';
import { motion } from 'framer-motion';

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { eventos, CAPITULOS, rsvps, toggleRsvp, currentUser, reactions, toggleReaction } = useApp();

  const evento = eventos.find(e => e.id === id);
  if (!evento) return <div className="p-20 text-center font-editorial text-2xl text-stone-500">Evento no encontrado.</div>;

  const capitulo = CAPITULOS.find(c => c.id === evento.capitulo_id);
  const isAttending = rsvps.some(r => r.eventId === evento.id && r.memberId === currentUser.id);
  const date = new Date(evento.fecha);

  const eventReactions = reactions.filter(r => r.item_id === evento.id);
  const hasReacted = eventReactions.some(r => r.member_id === currentUser?.id);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-4xl mx-auto space-y-10">
      <button onClick={() => navigate(-1)} className="group flex items-center gap-2 text-stone-400 hover:text-[#1B2A4E] transition-colors font-medium">
        <SafeIcon name="ArrowLeft" className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> Volver
      </button>

      <div className="bg-white rounded-[40px] shadow-sm border border-stone-100 overflow-hidden relative">
        {/* Subtle decorative background shape */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#74ACDF] rounded-full blur-[100px] opacity-10 -mr-20 -mt-20 pointer-events-none" />

        <div className="p-8 md:p-12 relative z-10">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="bg-[#F6B40E]/10 text-[#F6B40E] px-4 py-1.5 rounded-full label-technical text-xs flex items-center gap-1.5">
              <SafeIcon name="MapPin" className="w-3.5 h-3.5" />
              {capitulo?.nombre || 'Virtual'}
            </span>
            {evento.modalidad && (
              <span className="bg-stone-100 text-stone-500 px-4 py-1.5 rounded-full label-technical text-xs border border-stone-200">
                {evento.modalidad}
              </span>
            )}
          </div>

          <h1 className="text-4xl md:text-5xl font-editorial font-bold text-[#1B2A4E] mb-4 leading-tight">
            {evento.titulo}
          </h1>

          <p className="text-stone-500 text-lg leading-relaxed mb-10 max-w-2xl">
            {evento.descripcion}
          </p>

          <div className="flex flex-col sm:flex-row sm:items-center gap-6 p-6 md:p-8 bg-[#FAF9F6] rounded-[24px] border border-stone-100 mb-10">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-[#74ACDF] border border-stone-100">
                <SafeIcon name="Calendar" className="w-6 h-6" />
              </div>
              <div>
                <p className="font-bold text-[#1B2A4E] text-lg">{date.toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                <p className="text-sm font-medium text-stone-500">{date.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}</p>
              </div>
            </div>

            {evento.ubicacion && (
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-[#F6B40E] border border-stone-100">
                  <SafeIcon name="MapPin" className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-bold text-[#1B2A4E] text-lg">Ubicación</p>
                  <p className="text-sm font-medium text-stone-500">{evento.ubicacion}</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-4 border-t border-stone-100 pt-8">
            {!evento.isPast ? (
              <button 
                onClick={async () => { try { await toggleRsvp(evento.id); } catch (err) { console.error('toggleRsvp failed:', err); } }} 
                className={`px-8 py-4 rounded-2xl font-bold transition-all flex items-center justify-center gap-3 ${isAttending ? 'bg-stone-100 text-stone-500 hover:bg-stone-200' : 'bg-[#1B2A4E] text-white hover:bg-[#253969] shadow-lg hover:-translate-y-0.5'}`}
              >
                {isAttending ? <><SafeIcon name="Check" className="w-5 h-5" /> Confirmado</> : 'Confirmar Asistencia'}
              </button>
            ) : (
              <button className="px-8 py-4 rounded-2xl font-bold bg-[#74ACDF]/10 text-[#74ACDF] hover:bg-[#74ACDF]/20 transition-colors flex items-center justify-center gap-2">
                <SafeIcon name="History" className="w-5 h-5" /> Evento Pasado
              </button>
            )}

            <button
              onClick={async () => {
                try {
                  await toggleReaction(evento.id, 'evento');
                } catch (err) {
                  console.error('toggleReaction failed:', err);
                }
              }}
              className={`flex items-center gap-2 px-6 py-4 rounded-2xl font-bold transition-colors shadow-sm ${hasReacted ? 'bg-[#74ACDF]/10 text-[#74ACDF] border border-[#74ACDF]/20' : 'bg-white text-stone-500 border border-stone-200 hover:bg-stone-50'}`}
            >
              <SafeIcon name="ArrowUp" className="w-5 h-5" />
              Upvote ({eventReactions.length})
            </button>
          </div>
        </div>
      </div>

      <Comments itemId={evento.id} itemType="evento" />
    </motion.div>
  );
}